import { InfoWindow } from '@react-google-maps/api'
import cn from 'classnames'
import copy from 'copy-to-clipboard'
import { observer } from 'mobx-react'
import React, { PropsWithChildren, useId } from 'react'
import { Popup } from 'react-map-gl'

import { Button, Hierarchy, SubVariant } from '@flowwai-frontend/design-system'
import {
   CopyIcon3,
   GoogleEarthIcon,
   LinkExternal02,
   Trash03Icon
} from '@flowwai-frontend/design-system/icons'
import { isMobile } from '@flowwai-frontend/shared/utils/web'

import { MapsFeaturesEnum } from '../../constants'
import { useMapsUtilityContext } from '../../hooks/useMapsUtilityContext'
import { MapCoordinates } from '../../types'
import { redirectToGoogleEarth } from '../../utils'
import MapCoordinatesInputs from '../MapCoordinatesInputs/MapCoordinatesInputs'
import MapTooltip from '../MapTooltip/MapTooltip'

interface Props {
   index: number
   coordinates: MapCoordinates
   isReadOnly: boolean
   onUpdateCoordinate: (value: MapCoordinates, index: number) => void
   onDeleteCoordinates: (index: number) => void
   containerClassName?: string
   renderHeader?: () => React.ReactElement
   renderFooter?: () => React.ReactElement
}

const MapMarkerPopup = (props: Props): React.ReactElement => {
   const {
      index,
      coordinates,
      onUpdateCoordinate,
      isReadOnly,
      onDeleteCoordinates
   } = props

   const {
      serviceProvidersConfig: {
         maps: { serviceProvider: mapsServiceProvider }
      },
      supportedFeatures,
      mapsT,
      showCRMSuccessToast
   } = useMapsUtilityContext()

   const isMobileDevice = isMobile()
   const deleteButtonTooltipId = useId()

   const shouldRenderOpenInGoogleEarth =
      !isMobileDevice &&
      supportedFeatures?.includes(MapsFeaturesEnum.OpenInGoogleEarth)

   const PopupWrapper =
      mapsServiceProvider === 'GOOGLE_MAPS'
         ? GoogleMapsPopupWrapper
         : MapBoxPopupWrapper

   const onClickCopyCoordinates = (): void => {
      copy(`${coordinates.lat},${coordinates.lng}`)
      showCRMSuccessToast?.(mapsT('copySuccessMessage'))
   }

   const onClickOpenInGoogleEarth = (): void => {
      redirectToGoogleEarth(coordinates)
   }

   const renderDivider = (): React.ReactElement => (
      <div className='h-[10px] w-px bg-gray-300' />
   )

   const renderCopyCoordinatesButton = (): React.ReactElement => (
      <Button
         size={'ExtraSmall'}
         hierarchy={Hierarchy.Tertiary}
         subVariant={SubVariant.Gray}
         onClick={onClickCopyCoordinates}
         leftIcon={({ stroke }) => <CopyIcon3 fill={stroke} />}
         className='!p-0'
      >
         {mapsT('copy')}
      </Button>
   )

   const renderGoogleEarthButton = (): React.ReactElement => (
      <>
         {renderDivider()}

         <Button
            size={'ExtraSmall'}
            hierarchy={Hierarchy.Link}
            subVariant={SubVariant.Primary}
            onClick={onClickOpenInGoogleEarth}
            leftIcon={() => <GoogleEarthIcon />}
            rightIcon={({ stroke }) => (
               <LinkExternal02 stroke={stroke} height={14} width={14} />
            )}
         >
            {mapsT('googleEarth')}
         </Button>
      </>
   )

   const renderDeleteCoordinatesButton = (): React.ReactElement => (
      <>
         {renderDivider()}
         <Button
            size={'ExtraSmall'}
            hierarchy={Hierarchy.Link}
            subVariant={SubVariant.Destructive}
            onClick={() => onDeleteCoordinates(index)}
            leftIcon={({ stroke }) => <Trash03Icon stroke={stroke} />}
            isDisabled={isReadOnly}
            data-tooltip-id={deleteButtonTooltipId}
         >
            {mapsT('delete')}
         </Button>
         <MapTooltip
            content={mapsT('removeThisPoint')}
            tooltipId={deleteButtonTooltipId}
            place='right'
         />
      </>
   )

   const renderFooter = (): React.ReactElement => (
      <div className={cn('flex items-center mt-3 justify-between')}>
         {renderCopyCoordinatesButton()}

         {shouldRenderOpenInGoogleEarth ? renderGoogleEarthButton() : null}

         {renderDeleteCoordinatesButton()}
      </div>
   )

   return (
      <PopupWrapper coordinates={coordinates}>
         <div
            className={cn(
               'p-3',
               props.containerClassName,
               isMobileDevice ? 'w-[250px]' : 'w-[320px]'
            )}
         >
            {props.renderHeader?.()}

            <MapCoordinatesInputs
               value={coordinates}
               isReadOnly={isReadOnly}
               onChangeValue={value => onUpdateCoordinate(value, index)}
               alignment={isMobileDevice ? 'VERTICAL' : 'HORIZONTAL'}
            />

            {props.renderFooter?.() ?? renderFooter()}
         </div>
      </PopupWrapper>
   )
}

interface WrapperProps extends PropsWithChildren {
   coordinates: MapCoordinates
}

const GoogleMapsPopupWrapper = (props: WrapperProps) => (
   <InfoWindow
      position={props.coordinates}
      options={{
         pixelOffset: new google.maps.Size(0, -60),
         headerDisabled: true
      }}
   >
      {props.children}
   </InfoWindow>
)

const MapBoxPopupWrapper = (props: WrapperProps) => (
   <Popup
      longitude={props.coordinates.lng}
      latitude={props.coordinates.lat}
      maxWidth='320px'
      offset={48}
   >
      {props.children}
   </Popup>
)

export default observer(MapMarkerPopup)
